import React from 'react';
import { Box, Typography, Card, CardContent, CardActions, Button, Chip } from '@mui/material';
import type { SvgIconProps } from '@mui/material';
import { AdminPanelSettings } from '@mui/icons-material';
import { BRAND_COLORS } from '../../../styles/theme';

interface SimpleToolCardProps {
  /** Icon rendered both in the header and (by default) on the launch button. */
  icon: React.ReactElement<SvgIconProps>;
  title: string;
  description: string;
  buttonLabel: string;
  onLaunch: () => void;
  /** Optional status chip shown below the description (e.g. "XER Admin Only"). */
  chipLabel?: string;
  /** Overrides the button start icon; defaults to `icon`. */
  buttonIcon?: React.ReactElement;
  /** Optional data-testid forwarded to the launch button (e2e selectors). */
  testId?: string;
}

/**
 * Reusable launch-only admin tool card: header icon + title, description,
 * optional status chip, and a single full-width launch button.
 */
export const SimpleToolCard: React.FC<SimpleToolCardProps> = ({
  icon,
  title,
  description,
  buttonLabel,
  onLaunch,
  chipLabel,
  buttonIcon,
  testId,
}) => {
  return (
    <Card
      sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
      data-testid={testId ? `${testId}-card` : undefined}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          {React.cloneElement(icon, { sx: { mr: 1, color: BRAND_COLORS.forestGreen.main } })}
          <Typography variant="h6" component="h2">
            {title}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" paragraph>
          {description}
        </Typography>

        {chipLabel && (
          <Chip
            icon={<AdminPanelSettings />}
            label={chipLabel}
            color="primary"
            variant="outlined"
            sx={{ mt: 1 }}
          />
        )}
      </CardContent>

      <CardActions sx={{ p: 2, pt: 0 }}>
        <Button
          fullWidth
          variant="contained"
          onClick={onLaunch}
          startIcon={buttonIcon ?? icon}
          data-testid={testId}
          sx={{
            backgroundColor: BRAND_COLORS.forestGreen.main,
            '&:hover': {
              backgroundColor: BRAND_COLORS.forestGreen.main,
              opacity: 0.8,
            },
          }}
        >
          {buttonLabel}
        </Button>
      </CardActions>
    </Card>
  );
};
